/**
 * NotesPanel — append-only annotation panel for entity_notes.
 * Used on PO Detail, Receiving Detail (per-line + record-level), Directory
 * Distributors, and Discrepancies (post-resolve). Reads via get_entity_notes
 * RPC, writes via create_entity_note RPC. Visibility enforcement is RLS at
 * the database layer; this component renders the visibility pill so the
 * author can see what level they wrote.
 *
 * Realtime: subscribes to entity_notes postgres_changes filtered to
 * (entity_type, entity_id). On any change, refetch.
 *
 * Edit affordance: not implemented (V1 is append-only). RLS allows author
 * UPDATE within 15 min for typo fix; UI doesn't expose the path.
 */

import { useState, useEffect, useCallback, type JSX } from 'react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { extractEnvelope } from '../../lib/extractEnvelope';
import ArbiSelect from '../ui/ArbiSelect';

type EntityNotesType =
  | 'purchase_order'
  | 'receiving_record'
  | 'receiving_line_item'
  | 'discrepancy_resolution'
  | 'distributor'
  | 'transfer';

type NotesVisibility = 'admin_only' | 'all_internal' | 'dist_rep_visible';

interface NotesPanelProps {
  entityType: EntityNotesType;
  entityId: string;
  title?: string;
  readOnly?: boolean;
}

interface NoteRow {
  id: string;
  text: string;
  visibility: NotesVisibility;
  created_by: string | null;
  created_by_full_name: string | null;
  created_at: string;
  updated_at: string;
}

const VISIBILITY_OPTIONS: ReadonlyArray<{ value: NotesVisibility; label: string }> = [
  { value: 'all_internal', label: 'All internal' },
  { value: 'admin_only', label: 'Admin only' },
  { value: 'dist_rep_visible', label: 'Dist. Rep visible' },
];

const VISIBILITY_PILL: Record<NotesVisibility, string> = {
  admin_only: 'bg-danger/15 text-danger',
  all_internal: 'bg-info/15 text-info',
  dist_rep_visible: 'bg-success/15 text-success',
};

const VISIBILITY_LABEL: Record<NotesVisibility, string> = {
  admin_only: 'Admin only',
  all_internal: 'All internal',
  dist_rep_visible: 'Dist. Rep',
};

function relativeTime(iso: string): string {
  const d = new Date(iso).getTime();
  if (isNaN(d)) return '—';
  const m = Math.floor((Date.now() - d) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function NotesPanel({
  entityType,
  entityId,
  title = 'Notes',
  readOnly = false,
}: NotesPanelProps): JSX.Element {
  const { isDistRep, isAdmin } = useApp();
  const effectiveReadOnly = readOnly || isDistRep;

  const [notes, setNotes] = useState<NoteRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [draft, setDraft] = useState<string>('');
  const [draftVisibility, setDraftVisibility] = useState<NotesVisibility>('all_internal');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    const { data, error: rpcErr } = await supabase.rpc('get_entity_notes', {
      p_entity_type: entityType,
      p_entity_id: entityId,
    });
    if (rpcErr) {
      setError(rpcErr.message);
      setLoading(false);
      return;
    }
    setNotes((data as NoteRow[]) || []);
    setError(null);
    setLoading(false);
  }, [entityType, entityId]);

  useEffect(() => {
    load();
  }, [load]);

  // Realtime: refetch on any entity_notes change for this entity. The filter
  // can't combine entity_type + entity_id, so we filter by entity_id and
  // refetch (which re-applies the entity_type RPC filter server-side).
  useEffect(() => {
    const channel = supabase
      .channel(`entity_notes:${entityType}:${entityId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'entity_notes',
          filter: `entity_id=eq.${entityId}`,
        },
        () => {
          load();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [entityType, entityId, load]);

  async function handleSubmit(): Promise<void> {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setSubmitting(true);
    const rpcResult = await supabase.rpc('create_entity_note', {
      p_entity_type: entityType,
      p_entity_id: entityId,
      p_text: trimmed,
      p_visibility: draftVisibility,
    });
    setSubmitting(false);
    const env = extractEnvelope<unknown>(rpcResult);
    if (!env.isOk) {
      setError(env.userErrors[0]?.message ?? 'Save failed');
      return;
    }
    setDraft('');
    setDraftVisibility('all_internal');
    setError(null);
    await load();
  }

  return (
    <div className="bg-surface-1 border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink-bright">{title}</h3>
        {notes.length > 0 && (
          <span className="text-2xs text-ink-muted font-mono">{notes.length}</span>
        )}
      </div>

      {error && (
        <div className="px-3 py-2 rounded-md bg-danger/10 border border-danger/30 text-xs text-danger">
          {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-xs text-ink-muted">Loading…</div>
      ) : notes.length === 0 ? (
        <p className="text-xs text-ink-muted">{effectiveReadOnly ? 'No notes' : 'No notes yet'}</p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {notes.map((n) => {
            // dist_rep + viewer: hide visibility pill (they only ever see one tier);
            // admin/user: show all three pill colors so they can audit who-sees-what.
            const showPill = isAdmin || (!isDistRep && !effectiveReadOnly);
            return (
              <div
                key={n.id}
                className="bg-surface-2 border border-border-subtle rounded-md p-2.5 space-y-1"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-ink-primary font-medium truncate">
                      {n.created_by_full_name || 'Unknown'}
                    </span>
                    <span className="text-2xs text-ink-muted font-mono whitespace-nowrap">
                      {relativeTime(n.created_at)}
                    </span>
                  </div>
                  {showPill && (
                    <span
                      className={clsx(
                        'px-1.5 py-0.5 rounded text-2xs font-medium whitespace-nowrap',
                        VISIBILITY_PILL[n.visibility]
                      )}
                    >
                      {VISIBILITY_LABEL[n.visibility]}
                    </span>
                  )}
                </div>
                <p className="text-xs text-ink-secondary whitespace-pre-wrap break-words">
                  {n.text}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Composer */}
      {!effectiveReadOnly && (
        <div className="space-y-2 pt-2 border-t border-border-subtle">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a note…"
            className="arbi-input w-full text-xs resize-none min-h-[60px]"
            aria-label="Note text"
          />
          <div className="flex items-center justify-between gap-2">
            <div className="w-44">
              <ArbiSelect
                value={draftVisibility}
                onChange={(v) => setDraftVisibility(v as NotesVisibility)}
                options={[...VISIBILITY_OPTIONS]}
              />
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting || !draft.trim()}
              className={clsx(
                'arbi-btn-primary text-xs px-3 py-1',
                (submitting || !draft.trim()) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {submitting ? 'Adding…' : 'Add note'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
